import * as Sentry from '@sentry/react';
import axios from 'axios';
import { HttpBackendOptions } from 'i18next-http-backend';
import { defaultsDeep, set } from 'lodash';
import { AppLanguage } from 'types/general';
import localTranslations from '../../locales/dev.json';

const isTranslationEntry = (
  value: unknown,
): value is { translation?: string } =>
  typeof value === 'object' && value !== null && 'translation' in value;

/**
 * Ensures unified behavior with the backend so every translation is handled correctly regardless of its format.
 * Follows backend logic to cover both object and string values.
 */
export const parseLanguageResponse = (data: object): object => {
  const result = {};

  const walk = (value: unknown, path: string) => {
    if (typeof value === 'string') {
      if (value) set(result, path, value);
      return;
    }
    if (isTranslationEntry(value)) {
      if (value.translation) set(result, path, value.translation);
      return;
    }
    if (typeof value === 'object' && value !== null) {
      Object.entries(value).forEach(([key, child]) =>
        walk(child, path ? `${path}.${key}` : key),
      );
    }
  };

  walk(data, '');
  return result;
};

const LANGUAGE_KEY_BY_LANGUAGE: Record<AppLanguage, string> = {
  bg: 'bg_BG',
  bs: 'bs_BA',
  cs: 'cs_CZ',
  da: 'da_DK',
  de: 'de',
  el: 'el_GR',
  en: 'en',
  gb: 'en_GB',
  es: 'es',
  et: 'et_EE',
  fi: 'fi_FI',
  fr: 'fr',
  hr: 'hr_HR',
  hu: 'hu_HU',
  is: 'is',
  it: 'it',
  ka: 'ka_GE',
  lt: 'lt',
  lv: 'lv',
  mk: 'mk_MK',
  nl: 'nl_NL',
  no: 'no',
  pl: 'pl',
  pt: 'pt',
  ro: 'ro_RO',
  ru: 'ru',
  sk: 'sk_SK',
  sl: 'sl_SI',
  sq: 'sq_AL',
  sr: 'sr_Latn',
  sv: 'sv',
  tr: 'tr_TR',
  uk: 'uk_UA',
};

const loadLanguageResource = async (locale: string) => {
  const url = `https://translations.services.melp.com/locale/${locale}.json`;
  const response = await axios.get(url);
  const translationsResponse = parseLanguageResponse(response.data);
  if (process.env.NODE_ENV === 'development') {
    return defaultsDeep(
      {},
      parseLanguageResponse(localTranslations),
      translationsResponse,
    );
  }
  return translationsResponse;
};

export const languageRequest: HttpBackendOptions['request'] = async (
  _options,
  url,
  _payload,
  callback,
) => {
  try {
    const [rawLng] = url.split('|');
    const lng = rawLng.toLowerCase();
    if (lng === 'none') {
      Sentry.captureMessage("Requesting language 'none'");
      callback(null, {
        data: '{}',
        status: 200,
      });
      return;
    }
    const response = await loadLanguageResource(
      LANGUAGE_KEY_BY_LANGUAGE[lng as AppLanguage],
    );
    callback(null, {
      data: { ...response },
      status: 200,
    });
  } catch (e) {
    Sentry.captureException(e);
    callback(e, {
      status: 500,
      // As per documentation: "'res' should be an object <...> or null in case
      // of an error". Incorrect field type defined in official types.
      data: null as any,
    });
  }
};
